import { UseFormReturn } from 'react-hook-form'
import { ExternalLink } from 'lucide-react'
import { Input } from '@/components/ui/input'

// Must match the backend's PRODUCT_FILE_URL guard (furnisystems-backend
// src/operations/productFileSync.ts) byte-for-byte — every writer of a
// product-file URL shares that one pattern, and this is the client-side half
// of it, same convention as MAX_CUSTOMER_FILE_BYTES in CustomerFileRowEditor.
//
// Group 1 (the object key) is what the backend reads back to build s3_key. It
// is unused here — only `.test()` and zod `.regex()` matter on this side — and
// is carried anyway so the two copies stay diffable character for character.
// The extension is lowercase-only on purpose; the ERP parser rejects `.PDF`
// at the filename stage to agree with it.
export const PRODUCT_FILE_URL =
  /^https:\/\/sftp-furnisystems\.s3\.amazonaws\.com\/(vilmers\/4_PRODUCT_CATALOGUES\/[A-Za-z0-9._-]+\.pdf)(\?etag=[A-Za-z0-9]+(-[0-9]+)?)?$/

const PRODUCT_FILE_ORIGIN = 'https://sftp-furnisystems.s3.amazonaws.com/'

/**
 * A LOCAL MIRROR of how the backend keys a product file, for client-side
 * checks only. It is never sent: `ProductFileInput` has no `s3_key` field —
 * the backend derives the key itself from the url it has just validated, so a
 * client cannot point a key at an unrelated url or squat a slot the ERP import
 * is about to fill.
 *
 * The key is the url with the bucket origin and the `?etag=` cache-buster
 * stripped off, which is the raw S3 object key the ERP importer reads
 * (`BUCKET_URL + obj.Key` is how it builds the url in
 * furni-erp/src/import_product_catalogues.ts).
 *
 * Still worth computing here for two reasons: it catches two rows in the form
 * that resolve to the same object before the server throws on them, and it is
 * the identity behind the "changing this URL replaces the row" warning below.
 * Keep it in step with `deriveProductFileS3Key` in
 * furnisystems-backend/src/operations/productFileSync.ts.
 */
export const deriveProductFileS3Key = (url: string): string => {
  const withoutOrigin = url.startsWith(PRODUCT_FILE_ORIGIN)
    ? url.slice(PRODUCT_FILE_ORIGIN.length)
    : url
  return withoutOrigin.split('?')[0]
}

interface ProductFileRowEditorProps {
  form: UseFormReturn<any>
  basePath: string
  /**
   * The url this row currently has ON THE SERVER, for rows that exist there.
   * Omitted for rows the admin has just added. Used only to warn when an edit
   * would RE-KEY the row — see the note rendered below.
   */
  storedUrl?: string
}

export function ProductFileRowEditor({
  form,
  basePath,
  storedUrl,
}: ProductFileRowEditorProps) {
  const url: string = form.watch(`${basePath}.url`) ?? ''
  const urlValid = url.length === 0 || PRODUCT_FILE_URL.test(url)

  // The row's identity is its s3_key, and the key is the url with the origin
  // and the QUERY STRING stripped. So the comparison that matters here is
  // between derived keys, NOT between urls:
  //
  //   - same filename, different `?etag=`  -> same key -> the server updates
  //     the row in place and the id survives. This is the supplier-re-upload
  //     case and it must NOT warn; a claimed row survives it intact.
  //   - different filename                 -> different key -> the old key is
  //     absent from the desired set, so the server deletes that row and
  //     creates a new one.
  //
  // (Both verified against the running backend: an etag-only change kept the
  // row id, a filename change replaced it.)
  //
  // The second case is where the "claiming is one-directional and permanent"
  // promise in the Claim dialog can quietly come undone: deleting the row frees
  // the (product, s3_key) slot the claim was holding, and the next nightly
  // import is then free to attach that file again as an ERP-owned row. Nothing
  // server-side can prevent it -- replacing a file is a legitimate thing to
  // want -- so the admin is told before they save.
  const rekeyed =
    !!storedUrl &&
    url.length > 0 &&
    urlValid &&
    deriveProductFileS3Key(url) !== deriveProductFileS3Key(storedUrl)

  // Per-row errors don't reach the section-level <FormMessage>, so surface
  // them here or an empty/invalid row silently blocks submit. Walk basePath
  // rather than assuming the parent array's name — same as CustomerFileRowEditor.
  const rowErrors = basePath
    .split('.')
    .reduce((acc: any, key) => acc?.[key], form.formState.errors as any)

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <label className="text-sm font-medium">Display name</label>
        <Input
          placeholder="Name shown in the catalogue"
          {...form.register(`${basePath}.name`)}
        />
      </div>

      <div className="space-y-1">
        <label className="text-sm font-medium">Edition</label>
        <Input
          placeholder="e.g. EN, DE, NA, UK, NO, SE"
          {...form.register(`${basePath}.edition`)}
        />
        <p className="text-xs text-muted-foreground">
          Free-text market/language token taken from the supplier's filename —
          not one of the app's Language values.
        </p>
      </div>

      <div className="space-y-1">
        <label className="text-sm font-medium">S3 URL</label>
        <Input
          placeholder="https://sftp-furnisystems.s3.amazonaws.com/vilmers/4_PRODUCT_CATALOGUES/…pdf"
          {...form.register(`${basePath}.url`)}
        />
        {!urlValid && (
          <p className="text-xs text-destructive">
            Must be a sftp-furnisystems.s3.amazonaws.com/vilmers/4_PRODUCT_CATALOGUES/…pdf
            URL (an optional ?etag=… suffix is fine).
          </p>
        )}
        {rekeyed && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
            <p className="font-medium">
              This points at a different file, so it replaces the row.
            </p>
            <p>
              Saving deletes the existing row and creates a new one, because a
              product file is identified by its object key. If this row was
              claimed from the ERP import, the claim does not carry over — the
              nightly import becomes free to attach{' '}
              <span className="break-all font-mono">
                {deriveProductFileS3Key(storedUrl ?? '')}
              </span>{' '}
              to this product again on its own. To keep the claim, point this
              back at the same file (changing only the <code>?etag=</code> part
              is safe — that updates the row in place).
            </p>
          </div>
        )}
        {url && urlValid && (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
          >
            Open PDF
            <ExternalLink className="h-3.5 w-3.5 shrink-0" />
          </a>
        )}
      </div>

      {rowErrors?.name && (
        <p className="text-xs text-destructive">{rowErrors.name.message as string}</p>
      )}
      {rowErrors?.url && (
        <p className="text-xs text-destructive">{rowErrors.url.message as string}</p>
      )}
    </div>
  )
}
